import React from 'react';
import './FormSectionAtom.scss';

export type FormSectionVariant = 'default' | 'card' | 'bordered';

export interface FormSectionAtomProps {
  /** Content to render inside the section */
  children: React.ReactNode;
  /** Visual variant */
  variant?: FormSectionVariant;
  /** Minimum height to prevent layout jumping */
  minHeight?: string;
  /** Optional label/title for the section */
  label?: string;
  /** Additional CSS class */
  className?: string;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * FormSectionAtom
 *
 * A container for grouping form elements into logical zones.
 * Used to create stable layout regions that don't collapse when content changes.
 *
 * @example
 * <FormSectionAtom variant="card" minHeight="120px">
 *   <FrequencyDropdown />
 *   <WeekdayPicker />
 * </FormSectionAtom>
 */
export const FormSectionAtom: React.FC<FormSectionAtomProps> = ({
  children,
  variant = 'default',
  minHeight,
  label,
  className = '',
  'data-testid': testId,
}) => {
  const rootClasses = [
    'form-section',
    `form-section--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const style: React.CSSProperties = minHeight ? { minHeight } : {};

  return (
    <div className={rootClasses} style={style} data-testid={testId}>
      {label && <div className="form-section__label">{label}</div>}
      <div className="form-section__content">{children}</div>
    </div>
  );
};

export default FormSectionAtom;
